import React, { useState, useEffect } from 'react';
import { Alert, Badge, Button, Form, FormCheck } from 'react-bootstrap';
import { ListingItem } from '../utilities/CollectionGroup.tsx';
import { CollectionReptile } from '../collection/types/CollectionTypes.ts';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Lightbox from 'yet-another-react-lightbox';
import { geneExpressions } from '../../types/Gene.ts';

const ViewOnlyListing = ({
  reptile,
}: {
  reptile: CollectionReptile
}) => {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  return <>
    <Lightbox
      open={showPreview}
      close={() => setShowPreview(false)}
      slides={reptile.pictures.map((picture) => ({
        src: picture.url,
      }))}
    />
    <div style={{ overflowY: 'auto' }} className='d-block h-100 px-3 pb-5'>
      <div onClick={() => setShowPreview(true)} style={{
        cursor: 'pointer',
        backgroundImage: `url(${reptile.picture || '/images/notavailable.jpg'})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        height: '50vh',
        width: '100%',
        backgroundRepeat: 'no-repeat',
      }}></div>
      <div className='d-flex flex-column mt-3'>
        <h5 className='text-center'>Listing Price: ${reptile.listing_price?.price || 0}</h5>
        {reptile.listing_price?.firmPrice && <p className='text-center'>Firm on Price</p>}
        {!!reptile.genes.length && <div className='mb-2'>
          <strong className='mr-3'>
            Genes:
          </strong>
          {reptile.genes.map((gene, index) => (
            <Badge key={gene.id} bg={gene.isHet && gene.expression !== "SUPER" ? 'secondary' : 'primary'} className='mx-1 mb-1'>
              {gene.isHet ? (geneExpressions.find(g => g.value === gene.expression)?.label ?? '') : ''} {gene.gene_name}
            </Badge>
          ))}
        </div>}
        <p className='text-center'>{reptile.listing_price?.description || 'No description provided.'}</p>
      </div>

      <small className='text-center mt-3'>
        View more information about this animal by clicking the ( <FontAwesomeIcon icon={faBars} /> ) icon in the bottom right corner of the screen.
      </small>
    </div>
  </>
}

export const ListingPanel = ({
  reptile,
  onRemoveListing,
  setReptile,
  readOnly = false
}: {
  reptile: CollectionReptile,
  onRemoveListing?: () => void,
  setReptile: (reptile: CollectionReptile) => void,
  readOnly?: boolean
}) => {
  if (readOnly) {
    return <ViewOnlyListing reptile={reptile} />
  } else {
    return <EditListingPanel reptile={reptile} setReptile={setReptile} readOnly={readOnly} onRemoveListing={onRemoveListing} />
  }
}

export const EditListingPanel = ({
  reptile,
  setReptile,
  readOnly,
  onRemoveListing
}: {
  reptile: CollectionReptile,
  setReptile: (reptile: CollectionReptile) => void,
  readOnly: boolean,
  onRemoveListing?: () => void
}) => {
  const [listingPrice, setListingPrice] = useState<number>(reptile.listing_price?.price || 0);
  const [firmOnPrice, setFirmOnPrice] = useState<boolean>(reptile.listing_price?.firmPrice || false);
  const [listingDescription, setListingDescription] = useState<string>(reptile.listing_price?.description || '');
  const [updating, setUpdating] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);
  const [updateShare, setUpdateShare] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);


  const saveListing = () => {
    const data = new FormData();
    data.append("listing_price", listingPrice.toString());
    data.append("firm_price", firmOnPrice.toString());
    data.append("description", listingDescription);
    const url = reptile.listing_price ? `${(window as any).serverLocation}collection_reptile/${reptile.id}/listing/${reptile.listing_price?.id}/edit` : `${(window as any).serverLocation}collection_reptile/${reptile.id}/listing/create`;

    BasicFetch({
      url,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
        alert("Successfully saved changes")
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
      onFinish: () => {
        setUpdating(false);
      }
    })
  }

  const removeListing = () => {
    const url = `${(window as any).serverLocation}collection_reptile/${reptile.id}/move/nfs`;
    BasicFetch({
      url,
      method: 'POST',
      onSuccess: ({ reptile }) => {
        onRemoveListing?.();
        setReptile(reptile);
      },
      onFail: () => {
      },
      onFinish: () => {
        setMoving(false);
      }
    })
  }


  const moveReptileToAvailable = () => {
    const url = `${(window as any).serverLocation}collection_reptile/${reptile.id}/move/available`;
    BasicFetch({
      url,
      method: 'POST',
      onSuccess: ({ reptile }) => {
        setReptile(reptile);
      },
      onFail: () => {
      },
      onFinish: () => {
        setMoving(false);
      }
    })
  }

  const toggleSharing = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/user/toggle_sharing`,
      method: 'POST',
      data: {},
      onSuccess: ({ user }) => {
        alert('Sharing settings successfully turned on');
        (window as any).currentUser = user;
      },
      onFail: () => {
        alert('Failed to turn on sharing settings');
      },
      onFinish: () => {
        setUpdateShare(false);
      }
    })
  }

  useEffect(() => {
    if (updating) {
      saveListing();
    }
  }, [updating])

  useEffect(() => {
    if (moving) {
      moveReptileToAvailable();
    }
  }, [moving])

  useEffect(() => {
    if (updateShare) {
      toggleSharing();
    }
  }, [updateShare])

  const canList = reptile.status === 'available';
  return <div>
    {!canList && <Alert variant='warning'>
      <div className='d-flex justify-content-center'>
        <span className="mr-2">
          <FontAwesomeIcon size='lg' icon={faInfoCircle} />
        </span>
        <h5 className='my-auto' style={{ color: 'initial' }}>
          To create a listing for this reptile, you must resolve the following issues:
        </h5>
      </div>
    </Alert>}
    {reptile.status !== 'available' && <Alert className='bg-dark text-light' variant='info'>
      <div className='d-flex justify-content-center'>
        <span className="mr-2">
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
        <p className='my-auto'> This reptile is not listed as available. Would you like to change this reptile's status to Available?</p>
      </div>
      <div className='d-flex justify-content-center'>
        <Button variant='outline-light' className='mt-3' onClick={() => setMoving(true)}>Change to Available</Button>
      </div>
    </Alert>}
    {!(window as any).currentUser.available_reptiles_visible && <Alert className='bg-dark text-light' variant='info'>
      <div className='d-flex justify-content-center'>
        <span className="mr-2">
          <FontAwesomeIcon icon={faInfoCircle} />
        </span>
        <div className='my-auto'>
          <h5 className=''>Sharing is currently turned off.</h5>
          <p>You can still create a listing but it will not be accessible by other users until sharing is turned on. You can turn this off at any time.</p>
        </div>

      </div>
      <div className='d-flex justify-content-center'>
        <Button variant='outline-light' onClick={() => setUpdateShare(true)}>Turn on sharing</Button>
      </div>
    </Alert>}
    <h3>{reptile.listing_price ? 'Update' : 'Create'} Listing</h3>
    {canList && <><div className='col-lg-8 col-12 mx-auto'>
      <Form>
        <div className='d-flex flex-column'>
          <label htmlFor="listingPrice" className='mr-auto'>Listing Price</label>
          <input
            id='listingPrice'
            type='number'
            className='form-control'
            value={listingPrice}
            onChange={(event) => setListingPrice(parseInt(event.target.value) || 0)}
          />
        </div>
        <div className='d-flex flex-row mt-3'>
          <FormCheck
            id='firmOnPrice'
            className='mr-3 mt-1'
            checked={firmOnPrice}
            readOnly={readOnly}
            onClick={() => setFirmOnPrice(!firmOnPrice)}
          />
          <label className='my-auto ml-3' htmlFor="firmOnPrice">Firm on Price</label>
        </div>
        <div className='d-flex flex-column mt-3'>
          <label htmlFor="listingDescription" className='mr-auto'>Description</label>
          <textarea
            id='listingDescription'
            className='form-control'
            value={listingDescription}
            onChange={(event) => setListingDescription(event.target.value)}
          />
        </div>
      </Form>
      <div className='d-flex flex-column mt-4'>
        <Button className='col-lg-6 col-12 mx-auto' onClick={() => setUpdating(true)} disabled={updating}>
          Save
        </Button>
        <Button variant='outline-primary' className='mt-2 col-lg-6 col-12 mx-auto' onClick={() => setShowPreview(!showPreview)}>
          Preview
        </Button>
      </div>
      <Button className='mt-4 col-lg-6 col-12 mx-auto' variant='outline-danger' onClick={removeListing} disabled={moving}>
        Remove Listing & Move to Not For Sale
      </Button>
    </div>
    <Lightbox
      open={showPreview}
      close={() => setShowPreview(false)}
      slides={[{ src: reptile.picture || '/images/notavailable.jpg' }]}
      render={{
        iconNext: () => <></>,
        iconPrev: () => <></>,
        slide: () => (
          <ListingItem reptile={{
            ...reptile, listing_price: {
              id: -1,
              price: listingPrice,
              firmPrice: firmOnPrice,
              description: listingDescription
            }
          }} openDetailsModal={() => { }} />
        ),
      }}
    />
      <div className='d-none d-md-block'>
        <h5 className='my-3'>Preview:</h5>
        <div className='d-flex justify-content-center text-left'>


          <ListingItem reptile={{
            ...reptile, listing_price: {
              id: -1,
              price: listingPrice,
              firmPrice: firmOnPrice,
              description: listingDescription
            }
          }} openDetailsModal={() => { }} />
        </div>
      </div>
    </>}
  </div>
}